import { useEffect, useRef, useState } from "react";
import { ArrowUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import type { MessageAttachment } from "@/lib/looseend/types";
import { cn } from "@/lib/utils";

/**
 * Text composer for the hosted Chat turn.
 *
 * WP-PT — The former attachment menu (photo / camera / file / link) and the
 * voice button were prototype-only: attachments never left the browser and
 * were dropped by the hosted turn, and voice input had no handler. Neither is
 * offered until a governed path exists. Evidence files are attached to a Loop
 * through the Evidence panel, which is backend-authoritative.
 */
export function ChatComposer({
  onSend,
  disabled,
  placeholder = "Tell LooseEnd what's unresolved…",
  autoFocus = true,
}: {
  onSend: (text: string, attachments?: MessageAttachment[]) => void;
  disabled?: boolean;
  placeholder?: string;
  autoFocus?: boolean;
}) {
  const [value, setValue] = useState("");
  const ref = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 180)}px`;
  }, [value]);

  useEffect(() => {
    if (autoFocus) ref.current?.focus();
  }, [autoFocus]);

  useEffect(() => {
    if (!disabled) ref.current?.focus();
  }, [disabled]);

  const canSend = Boolean(value.trim());

  const submit = () => {
    if (!canSend || disabled) return;
    onSend(value.trim());
    setValue("");
  };

  return (
    <div className="content-column">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <div
          className={cn(
            "flex items-end gap-1 rounded-[14px] border border-border bg-card px-3 py-1.5 transition-colors",
            "focus-within:border-primary/40 focus-within:ring-4 focus-within:ring-primary/10",
          )}
        >
          <label htmlFor="composer" className="sr-only">
            Message
          </label>
          <textarea
            id="composer"
            ref={ref}
            rows={1}
            value={value}
            disabled={disabled}
            placeholder={placeholder}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                submit();
              }
            }}
            className="max-h-[180px] min-h-9 flex-1 resize-none bg-transparent py-2 text-[15px] leading-relaxed text-foreground outline-none placeholder:text-muted-foreground"
          />

          <Button
            type="submit"
            size="icon"
            className="size-9 shrink-0 rounded-[10px]"
            aria-label="Send message"
            disabled={disabled || !canSend}
          >
            {disabled ? (
              <span className="size-3.5 animate-spin rounded-full border-2 border-primary-foreground/40 border-t-primary-foreground" />
            ) : (
              <ArrowUp className="size-[18px]" aria-hidden="true" />
            )}
          </Button>
        </div>
      </form>

      <p className="mt-2 text-center text-xs text-muted-foreground">
        L∞seEnd™ can make mistakes. Verify important information before acting.
      </p>
    </div>
  );
}
